import { useLayoutEffect, useRef, type ReactNode } from "react";

/** Matches the sliding pill in `SegmentedTabs`, so the control and the card it belongs to settle together. */
const DURATION_MS = 260;
const EASE = "cubic-bezier(.2,0,0,1)";

interface AnimatedHeightProps {
  children: ReactNode;
  /**
   * Classes for the measured inner box — where the region's padding belongs.
   * The outer box is deliberately styling-free: it owns the animated height,
   * and under `border-box` sizing any padding on it would eat into that height.
   */
  innerClassName?: string;
}

/**
 * Animates its own height whenever its children's rendered height changes.
 *
 * Why: a tabbed card whose tabs hold different row counts re-flows the instant
 * the tab changes, so everything below it on the page snaps up or down. The
 * content swap is already abrupt enough to read as a jump-cut; the rest of the
 * page jumping with it is what makes it feel broken. Animating the height lets
 * the panels underneath glide into their new place instead.
 *
 * The box only carries a pixel height *while* it is animating, and returns to
 * `auto` on `transitionend`. Height has to be a number for the browser to
 * interpolate it, but a box left pinned to a number clips its own content the
 * next time anything reflows it without notifying us — a wrapped row, a font
 * swap, a resize delivered while the tab was hidden. `auto` at rest means the
 * worst case is a missed animation rather than unreachable content.
 *
 * This runs entirely on refs: a height animation is not state anything else
 * renders from, and re-rendering the subtree on every observed resize would
 * make a column drag re-render the whole list.
 *
 * `overflow-hidden` clips whatever does not fit mid-transition, so this is only
 * safe around content that has nothing escaping its own bounds (no popovers,
 * dropdowns or tooltips anchored inside).
 */
const AnimatedHeight = ({ children, innerClassName }: AnimatedHeightProps) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let content = inner.getBoundingClientRect().height;

    // Mounting empty means the query behind the list had not resolved yet, so
    // the first content to arrive is the initial render finishing rather than
    // anything the user did — it appears at its own size instead of unfolding
    // from nothing. Later empty-to-filled changes (a tab whose filter matches
    // nothing) are swaps, and animate like any other.
    let primed = content > 0;

    const release = () => {
      outer.style.transition = "";
      outer.style.height = "";
    };

    // Only `transitionend` on our own height counts: a row's `transition-colors`
    // bubbles through here too, and releasing on one of those would drop the
    // height mid-animation.
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target === outer && event.propertyName === "height") release();
    };

    // A ResizeObserver rather than reacting to `children`: the height also
    // changes when the column narrows, a font loads, or a row's text wraps —
    // none of which is a React render this component can see.
    const observer = new ResizeObserver(() => {
      const next = inner.getBoundingClientRect().height;
      if (next === content) return;

      // Mid-animation the outer box already carries a pixel height, so its live
      // height is the value being shown and an interrupted animation continues
      // from where it got to. At rest the box is `auto` and has already
      // re-flowed to `next`, so the previous measurement is the start value.
      const from = outer.style.height ? outer.getBoundingClientRect().height : content;
      content = next;

      // Both no-animation cases still release rather than just returning: the
      // box may be mid-animation from before the setting changed, and it has to
      // end up back on `auto` either way.
      if (reduced.matches || !primed) {
        primed = true;
        release();
        return;
      }

      outer.style.transition = "none";
      outer.style.height = `${from}px`;
      // Flush the start value un-transitioned. Without the forced reflow both
      // writes land in one style recalculation and there is nothing to animate
      // from.
      void outer.offsetHeight;
      outer.style.transition = `height ${DURATION_MS}ms ${EASE}`;
      outer.style.height = `${next}px`;
    });

    outer.addEventListener("transitionend", onTransitionEnd);
    observer.observe(inner);

    return () => {
      outer.removeEventListener("transitionend", onTransitionEnd);
      observer.disconnect();
      release();
    };
  }, []);

  return (
    <div ref={outerRef} className="overflow-hidden">
      <div ref={innerRef} className={innerClassName}>
        {children}
      </div>
    </div>
  );
};

export default AnimatedHeight;
